/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * useMemoRef.ts - Like useMemo, but returns a ref.
 */

import { DependencyList, MutableRefObject, useRef } from 'react';


/**
 * Like {@link useMemo}, except it updates a ref rather than producing a new value.
 * 
 * The main use case for this is that other hooks can reference this ref (including it as a dependency), and avoid
 * getting re-run when the value of the ref is updated, since the ref itself keeps the same identity. This can be
 * useful for hooks that are imperative in nature, such as those dealing with timers or animations, where re-running
 * the hook should only occur at specific times.
 *
 * Since this is an imperative equivalent of useMemo, and react prefers a functional approach, it should be used
 * sparingly. Whenever it's acceptable to re-run your hooks, use useMemo instead.
 * 
 * @param factory - a function to generate the value
 * @param deps - dependencies of `factory` - if any of these change, `factory` is re-run, and if undefined
 *   `factory` is run every render.
 * @returns a ref object containing the memoized value
 */
export const useMemoRef = <T> (
  factory: () => T, 
  deps: DependencyList | undefined
): MutableRefObject<T> => {
  const ref = useRef<T | typeof uninitialized>(uninitialized);
  const lastDeps = useRef<DependencyList | undefined>(deps);

  if (ref.current === uninitialized ||
      lastDeps.current === undefined ||
      deps === undefined ||
      !lastDeps.current.every((el, index) => el === deps[index])) {
    ref.current = factory();
  }

  lastDeps.current = deps;

  return ref as MutableRefObject<T>;
};

/**
 * Like {@link useCallback}, except it updates a ref rather than producing a new value.
 * @see useMemoRef
 * 
 * @param callback - the callback function to use this render (if no deps are different, this is discarded and the old
 *   value for the function is used)
 * @param deps - dependencies of `callback`
 * @returns a ref object containing the callback function to use
 */
export const useCallbackRef = <T extends (...args: never[]) => unknown> (
  callback: T,
  deps: DependencyList
): MutableRefObject<T> => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemoRef(() => callback, deps);
};

/**
 * Represents the state where the ref is not yet initialized.
 * This symbol is safer than using `undefined` or `null` since it is not exported, so it can't ever be the return
 * value of `factory`.
 */
const uninitialized = Symbol('uninitialized');
